import { createSentryPiniaPlugin } from '@sentry/vue'
import { createPinia } from 'pinia'
import { type App, type Reactive } from 'vue'

import { useApmEvents } from './apmEvents.ts'
import { useArchivedSources } from './archivedSources.ts'
import { useAvatars } from './avatars.ts'
import { useCase } from './case.ts'
import { useCases } from './cases.ts'
import { useCountries } from './countries.ts'
import { useDataSources } from './dataSources.ts'
import { useDateOptions } from './dateOptions.ts'
import { useFlags } from './flags.ts'
import { useGroups } from './groups.ts'
import { useInitialState } from './initialState.ts'
import { useLoading } from './loading.ts'
import { useLocations } from './locations.ts'
import { useOrganization } from './organization.ts'
import { useOrganizationFunctions } from './organizationFunctions.ts'
import { useOrganizationIntegration } from './organizationIntegration.ts'
import { usePolicies } from './policies.ts'
import { useReportSettings } from './reportSettings.ts'
import { useRiskConfig } from './riskConfig.ts'
import { useRiskSourceConfigs } from './riskSourceConfigs.ts'
import { useRunningIntelligentCheckJobs } from './runningIntelligentCheckJobs.ts'
import { useSessions } from './sessions.ts'
import { useSettings } from './settings.ts'
import { useSource } from './source.ts'
import { useTransactionMonitoringRiskConfig } from './transactionMonitoringRiskConfig.ts'
import { useTranslation } from './translation.ts'
import { useUser } from './user.ts'
import { useUsers } from './users.ts'

export interface Stores {
  apmEvents: ReturnType<typeof useApmEvents>
  archivedSources: ReturnType<typeof useArchivedSources>
  avatars: ReturnType<typeof useAvatars>
  case: ReturnType<typeof useCase>
  cases: ReturnType<typeof useCases>
  countries: ReturnType<typeof useCountries>
  dataSources: ReturnType<typeof useDataSources>
  dateOptions: ReturnType<typeof useDateOptions>
  flags: ReturnType<typeof useFlags>
  groups: ReturnType<typeof useGroups>
  initialState: ReturnType<typeof useInitialState>
  loading: ReturnType<typeof useLoading>
  locations: ReturnType<typeof useLocations>
  organization: ReturnType<typeof useOrganization>
  organizationFunctions: ReturnType<typeof useOrganizationFunctions>
  organizationIntegration: ReturnType<typeof useOrganizationIntegration>
  policies: ReturnType<typeof usePolicies>
  reportSettings: ReturnType<typeof useReportSettings>
  riskConfig: ReturnType<typeof useRiskConfig>
  riskSourceConfigs: ReturnType<typeof useRiskSourceConfigs>
  transactionMonitoringRiskConfig: ReturnType<typeof useTransactionMonitoringRiskConfig>
  sessions: ReturnType<typeof useSessions>
  settings: ReturnType<typeof useSettings>
  source: ReturnType<typeof useSource>
  translation: ReturnType<typeof useTranslation>
  user: ReturnType<typeof useUser>
  users: ReturnType<typeof useUsers>
  runningIntelligentCheckJobs: ReturnType<typeof useRunningIntelligentCheckJobs>
}

export const stores = {
  install(app: App) {
    const stores: Stores = {
      apmEvents: useApmEvents(),
      archivedSources: useArchivedSources(),
      avatars: useAvatars(),
      case: useCase(),
      cases: useCases(),
      countries: useCountries(),
      dataSources: useDataSources(),
      dateOptions: useDateOptions(),
      flags: useFlags(),
      groups: useGroups(),
      initialState: useInitialState(),
      loading: useLoading(),
      locations: useLocations(),
      organization: useOrganization(),
      organizationFunctions: useOrganizationFunctions(),
      organizationIntegration: useOrganizationIntegration(),
      policies: usePolicies(),
      reportSettings: useReportSettings(),
      riskConfig: useRiskConfig(),
      riskSourceConfigs: useRiskSourceConfigs(),
      transactionMonitoringRiskConfig: useTransactionMonitoringRiskConfig(),
      sessions: useSessions(),
      settings: useSettings(),
      source: useSource(),
      translation: useTranslation(),
      user: useUser(),
      users: useUsers(),
      runningIntelligentCheckJobs: useRunningIntelligentCheckJobs(),
    }

    window.stores = stores
    app.config.globalProperties.$stores = stores
  },
}

export const pinia = createPinia()

pinia.use(createSentryPiniaPlugin())

pinia.use(({ options, store }) => {
  if (!options.init) {
    return
  }

  store.$initialized = new Promise((resolve, reject) => {
    if (!options.init) {
      resolve(store.$state)

      return
    }

    const result = options.init(store)

    if (result instanceof Promise) {
      result.then(() => resolve(store.$state)).catch((error) => reject(`Store \`${store.$id}\` init failed: ${error}`))
    } else {
      resolve(store.$state)
    }
  })
})

/**
 * Resets the state of a composition store.
 * @param state Object containing all the values that should be reset.
 */
export function withReset(state: Record<string, Ref | Reactive<unknown>>): () => void {
  const initialState: Record<string, unknown> = {}

  for (const key in state) {
    const value = state[key]
    if (isReadonly(value)) continue

    if (isRef(value)) {
      initialState[key] = value.value
    } else {
      initialState[key] = copyObject(value)
    }
  }

  return () => {
    for (const key in state) {
      const value = state[key]
      if (isReadonly(value)) continue

      if (isRef(value)) {
        value.value = initialState[key]
      } else {
        for (const valueKey in value) {
          delete value[valueKey as keyof typeof value]
        }

        Object.assign(value, copyObject(initialState[key]))
      }
    }
  }
}
